import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, TrendingUp, Trash2, DollarSign, User, Calendar, GripVertical, CircleDot, Trophy, XCircle } from "lucide-react";
import { toast } from "sonner";

const STAGES = [
  { id: "lead",        label: "Lead",        color: "#6B7280", bg: "rgba(107,114,128,0.10)" },
  { id: "qualified",   label: "Qualified",   color: "#3B82F6", bg: "rgba(59,130,246,0.10)" },
  { id: "proposal",    label: "Proposal",    color: "#8B5CF6", bg: "rgba(139,92,246,0.10)" },
  { id: "negotiation", label: "Negotiation", color: "#F59E0B", bg: "rgba(245,158,11,0.10)" },
  { id: "won",         label: "Won",         color: "#22C55E", bg: "rgba(34,197,94,0.10)" },
  { id: "lost",        label: "Lost",        color: "#EF4444", bg: "rgba(239,68,68,0.10)" },
];

const EMPTY_FORM = { title: "", contact_name: "", contact_email: "", value: "", stage: "lead", close_date: "", notes: "" };

function formatValue(v) {
  if (!v) return "—";
  const n = parseFloat(v);
  if (isNaN(n)) return "—";
  return n >= 1000 ? `$${(n/1000).toFixed(1)}k` : `$${n.toLocaleString()}`;
}

function DealCard({ deal, onEdit, onDelete, onDragStart }) {
  return (
    <div
      draggable
      onDragStart={(e) => { e.dataTransfer.setData("dealId", deal.id); onDragStart(deal.id); }}
      data-testid={`deal-card-${deal.id}`}
      className="card-premium p-3 mb-2 cursor-grab active:cursor-grabbing hover:shadow-md transition-all select-none"
      style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10 }}
      onClick={() => onEdit(deal)}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm truncate" style={{ color: "var(--text)" }}>{deal.title}</div>
          {deal.contact_name && (
            <div className="flex items-center gap-1 mt-0.5">
              <User className="w-3 h-3 shrink-0" style={{ color: "var(--text-xsoft)" }} />
              <span className="text-xs truncate" style={{ color: "var(--text-soft)" }}>{deal.contact_name}</span>
            </div>
          )}
        </div>
        <div className="shrink-0 text-right">
          <div className="text-sm font-bold" style={{ color: "var(--brand)", fontFamily: "var(--font-sans)" }}>{formatValue(deal.value)}</div>
        </div>
      </div>
      {deal.close_date && (
        <div className="flex items-center gap-1 mt-1.5 text-xs" style={{ color: "var(--text-xsoft)" }}>
          <Calendar className="w-3 h-3" /> {deal.close_date}
        </div>
      )}
    </div>
  );
}

export default function Pipeline() {
  const [deals, setDeals] = useState([]);
  const [summary, setSummary] = useState({});
  const [open, setOpen] = useState(false);
  const [editDeal, setEditDeal] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [dragging, setDragging] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  const load = async () => {
    const [{ data: d }, { data: s }] = await Promise.all([
      api.get("/deals"),
      api.get("/deals/pipeline-summary"),
    ]);
    setDeals(d);
    setSummary(s);
  };
  useEffect(() => { load(); }, []);

  const openCreate = (stage = "lead") => {
    setEditDeal(null);
    setForm({ ...EMPTY_FORM, stage });
    setOpen(true);
  };

  const openEdit = (deal) => {
    setEditDeal(deal);
    setForm({ ...deal, value: String(deal.value || "") });
    setOpen(true);
  };

  const save = async () => {
    if (!form.title) { toast.error("Title required"); return; }
    const payload = { ...form, value: parseFloat(form.value) || 0 };
    try {
      if (editDeal) {
        await api.patch(`/deals/${editDeal.id}`, payload);
        toast.success("Deal updated");
      } else {
        await api.post("/deals", payload);
        toast.success("Deal created");
      }
      setOpen(false);
      await load(); // await ensures the Kanban refreshes immediately
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (id) => {
    await api.delete(`/deals/${id}`);
    toast.success("Deal removed");
    load();
  };

  const onDrop = async (e, targetStage) => {
    e.preventDefault();
    setDragOver(null);
    const dealId = e.dataTransfer.getData("dealId");
    if (!dealId) return;
    const deal = deals.find(d => d.id === dealId);
    if (!deal || deal.stage === targetStage) return;
    setDeals(prev => prev.map(d => d.id === dealId ? { ...d, stage: targetStage } : d));
    try {
      await api.patch(`/deals/${dealId}`, { stage: targetStage });
      load();
    } catch { toast.error("Failed to move deal"); }
    setDragging(null);
  };

  const totalPipeline = deals.filter(d => d.stage !== "lost").reduce((sum, d) => sum + (parseFloat(d.value) || 0), 0);
  const wonDeals = deals.filter(d => d.stage === "won");
  const wonValue = wonDeals.reduce((sum, d) => sum + (parseFloat(d.value) || 0), 0);

  return (
    <div className="p-6 space-y-6 h-full" data-testid="pipeline-page">
      {/* Header */}
      <div className="rise flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: "var(--font-heading)", color: "var(--text)" }}>Deal Pipeline</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-soft)" }}>Drag deals across stages — from first touch to closed won.</p>
        </div>
        <Button onClick={() => openCreate()} className="gap-2 text-white" style={{ background: "var(--brand)" }} data-testid="add-deal-btn">
          <Plus className="w-4 h-4" /> Add Deal
        </Button>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Total pipeline", value: `$${totalPipeline.toLocaleString()}`, color: "var(--brand)" },
          { label: "Won value", value: `$${wonValue.toLocaleString()}`, color: "var(--success)" },
          { label: "Total deals", value: deals.length, color: "var(--text)" },
        ].map(({ label, value, color }) => (
          <Card key={label} className="rise card-premium p-4 flex items-center gap-3" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
            <div className="text-2xl font-bold" style={{ color, fontFamily: "var(--font-sans)" }}>{value}</div>
            <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--text-soft)" }}>{label}</div>
          </Card>
        ))}
      </div>

      {/* Kanban Board */}
      <div className="flex gap-4 overflow-x-auto pb-4" style={{ minHeight: 500 }}>
        {STAGES.map((stage) => {
          const stageDeals = deals.filter(d => d.stage === stage.id);
          const stageTotal = stageDeals.reduce((sum, d) => sum + (parseFloat(d.value) || 0), 0);
          const isOver = dragOver === stage.id;
          return (
            <div
              key={stage.id}
              data-testid={`stage-col-${stage.id}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(stage.id); }}
              onDragLeave={() => setDragOver(null)}
              onDrop={(e) => onDrop(e, stage.id)}
              className="flex-shrink-0 rounded-2xl transition-all"
              style={{
                width: 220,
                background: isOver ? stage.bg : "var(--bg)",
                border: `1px solid ${isOver ? stage.color : "var(--border)"}`,
                padding: 12,
              }}
            >
              {/* Column header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: stage.color }} />
                  <span className="font-semibold text-sm" style={{ color: "var(--text)" }}>{stage.label}</span>
                  <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: stage.bg, color: stage.color }}>{stageDeals.length}</span>
                </div>
                <button onClick={() => openCreate(stage.id)} className="w-6 h-6 rounded-full flex items-center justify-center hover:opacity-80 transition-all" style={{ background: stage.bg, color: stage.color }}>
                  <Plus className="w-3 h-3" />
                </button>
              </div>
              {stageTotal > 0 && (
                <div className="text-xs font-bold mb-2 font-sans" style={{ color: stage.color }}>{formatValue(stageTotal)}</div>
              )}
              {/* Deal Cards */}
              <div style={{ minHeight: 60 }}>
                {stageDeals.map(deal => (
                  <DealCard
                    key={deal.id}
                    deal={deal}
                    onEdit={openEdit}
                    onDelete={remove}
                    onDragStart={setDragging}
                  />
                ))}
                {stageDeals.length === 0 && (
                  <div className="text-center py-6 text-xs" style={{ color: "var(--text-xsoft)" }}>Drop here</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Create / Edit Deal Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editDeal ? "Edit Deal" : "New Deal"}</DialogTitle>
            <DialogDescription>Fill in the deal details. Drag to move between stages.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Title *</Label><Input data-testid="deal-title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Enterprise license renewal" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Contact name</Label><Input data-testid="deal-contact-name" value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} placeholder="Alex Rivera" /></div>
              <div><Label>Contact email</Label><Input data-testid="deal-contact-email" value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })} placeholder="alex@co.com" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Value ($)</Label><Input data-testid="deal-value" type="number" value={form.value} onChange={e => setForm({ ...form, value: e.target.value })} placeholder="5000" /></div>
              <div><Label>Stage</Label>
                <Select value={form.stage} onValueChange={v => setForm({ ...form, stage: v })}>
                  <SelectTrigger data-testid="deal-stage"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STAGES.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Close date</Label><Input data-testid="deal-close-date" type="date" value={form.close_date || ""} onChange={e => setForm({ ...form, close_date: e.target.value })} /></div>
            <div><Label>Notes</Label><Textarea data-testid="deal-notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} /></div>
          </div>
          <DialogFooter className="flex gap-2">
            {editDeal && (
              <Button variant="outline" onClick={() => { remove(editDeal.id); setOpen(false); }} className="gap-1 text-red-500 border-red-200">
                <Trash2 className="w-3 h-3" /> Delete
              </Button>
            )}
            <Button onClick={save} className="text-white ml-auto" style={{ background: "var(--brand)" }} data-testid="deal-save-btn">
              {editDeal ? "Save changes" : "Create deal"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
